/**
 * CheckoutSkeleton.tsx used in src/pages/Checkout.tsx to display a skeleton
 * loading state while the CheckoutForm is loading.
 *
 * Author: Victor Chevillotte <victor.chevillotte@ircamamplify.com>
 * Copyright © Ircam amplify 2025
 */
import React from 'react'
import Skeleton from 'react-loading-skeleton'
import 'react-loading-skeleton/dist/skeleton.css'

// Skeleton component that mirrors the layout of the real CheckoutForm.
const CheckoutSkeleton: React.FC = () => {
  // Container styles similar to the real form.
  const containerStyle: React.CSSProperties = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '2rem',
  }

  const columnStyle: React.CSSProperties = {
    flex: '1 1 300px',
  }

  const cardStyle: React.CSSProperties = {
    background: '#fff',
    padding: '1.5rem',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    marginBottom: '1rem',
  }

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '1rem' }}>
      <div style={containerStyle}>
        {/* Left Column: Billing Information */}
        <div style={columnStyle}>
          <div style={{ ...cardStyle, marginBottom: '1rem' }}>
            {/* Heading Skeleton for "Billing Information" */}
            <Skeleton
              height={30}
              width={200}
              style={{ marginBottom: '1rem' }}
            />
            {/* Skeleton for AddressElement */}
            <Skeleton height={40} style={{ marginBottom: '1rem' }} />
            {/* Skeleton for Tax ID Collection Form fields */}
            <Skeleton
              height={20}
              width={150}
              style={{ marginBottom: '0.5rem' }}
            />{' '}
            {/* Label: Business Name */}
            <Skeleton height={40} style={{ marginBottom: '1rem' }} />{' '}
            {/* Business Name input */}
            <Skeleton
              height={20}
              width={150}
              style={{ marginBottom: '0.5rem' }}
            />{' '}
            {/* Label: Tax ID */}
            <Skeleton height={40} style={{ marginBottom: '1rem' }} />{' '}
            {/* Tax ID input */}
          </div>
        </div>

        {/* Right Column: Order Summary & Payment */}
        <div style={columnStyle}>
          {/* Order Summary Card */}
          <div style={{ ...cardStyle, marginBottom: '1rem' }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '2rem',
              }}
            >
              <Skeleton height={30} width={150} /> {/* Order Summary Title */}
              <Skeleton height={20} width={100} /> {/* "Change Plan" link */}
            </div>
            {/* Promo code section skeleton */}
            <div
              style={{ marginBottom: '1rem', display: 'flex', gap: '0.5rem' }}
            >
              <Skeleton
                height={40}
                width={200}
                style={{ marginBottom: '1rem' }}
              />{' '}
              {/* Promo code input */}
              <Skeleton
                height={40}
                width={80}
                style={{ marginBottom: '1rem' }}
              />{' '}
              {/* Apply button */}
            </div>
            <Skeleton
              height={30}
              width={250}
              style={{ marginBottom: '1rem' }}
            />{' '}
            {/* Plan Summary */}
            <Skeleton
              height={25}
              width={200}
              style={{ marginBottom: '2rem' }}
            />{' '}
            {/* Plan Summary */}
            <Skeleton
              height={20}
              width={400}
              style={{ marginBottom: '1rem' }}
            />{' '}
            {/* Invoice date message */}
          </div>

          {/* Payment Card */}
          <div style={{ ...cardStyle, marginBottom: '1rem' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '1rem',
              }}
            >
              <Skeleton
                height={30}
                width={100}
                style={{ marginBottom: '1rem' }}
              />{' '}
              {/* Payment header */}
              <Skeleton
                height={25}
                width={50}
                style={{ marginLeft: '1rem' }}
              />{' '}
              {/* Stripe logo placeholder */}
            </div>
            <Skeleton height={200} style={{ marginBottom: '1rem' }} />{' '}
            {/* PaymentElement mimic */}
          </div>

          {/* Pay Button Skeleton */}
          <Skeleton height={50} width="100%" style={{ marginTop: '1rem' }} />
        </div>
      </div>
    </div>
  )
}

export default CheckoutSkeleton
